import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Plus, Users, Pencil, Trash2, X, FolderOpen, Loader2, Link2, Coffee, KeyRound, Calculator, Download, MailPlus, FileText,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { CafeChat } from "@/components/CafeChat";
import { CostEstimator } from "@/components/CostEstimator";
import { CessionSimulator } from "@/components/CessionSimulator";
import { useAuth } from "@/context/AuthContext";
import FileManager from "@/components/FileManager";

const EMPTY = { code: "", name: "", email: "", password: "", drive_url: "", extra_emails: "" };
const byCode = (a, b) => a.code.localeCompare(b.code, "fr");
const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const letterOf = (code) => {
  const c = (code || "#")[0].toUpperCase();
  return LETTERS.includes(c) ? c : "#";
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [showImportXls, setShowImportXls] = useState(false);
  const [xlsFile, setXlsFile] = useState(null);
  const [xlsBusy, setXlsBusy] = useState(false);
  const [xlsResult, setXlsResult] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [cafeOpen, setCafeOpen] = useState(false);
  const [cafeUnread, setCafeUnread] = useState(0);
  const [activeLetter, setActiveLetter] = useState(null);
  const [stats, setStats] = useState(null);
  const [credBusy, setCredBusy] = useState(false);
  const [showEstimator, setShowEstimator] = useState(false);
  const [showCession, setShowCession] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const syncEmails = async () => {
    setSyncBusy(true); setSyncResult(null);
    try {
      const r = await api.post("/admin/clients/sync-emails");
      setSyncResult(r.data);
      const n = r.data.updated?.length || 0;
      if (n) {
        toast.success(`${n} client(s) enrichi(s) : ${r.data.updated.map((u) => u.code).join(", ")}`);
        load();
      } else toast.info("Aucune nouvelle adresse à rattacher — tout est déjà à jour.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Synchronisation impossible.");
    } finally { setSyncBusy(false); }
  };

  const sendCredentials = async () => {
    if (!selected) return;
    if (!window.confirm(`Un NOUVEAU mot de passe sera généré pour ${selected.name || selected.code} et envoyé à ${selected.email}.\n\nL'ancien mot de passe ne fonctionnera plus. Continuer ?`)) return;
    setCredBusy(true);
    try {
      const r = await api.post(`/admin/clients/${selected.id}/send-credentials`);
      toast.success(`Email envoyé à ${r.data.email} — nouveau mot de passe : ${r.data.password}`, { duration: 30000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible.");
    } finally { setCredBusy(false); }
  };

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  // Ouvre automatiquement la lettre du client sélectionné (ex : après création)
  useEffect(() => { if (selected) setActiveLetter(letterOf(selected.code)); }, [selected?.id]);

  // Factures « À payer » du client sélectionné (mêmes données que son onglet client)
  const [clientInvoices, setClientInvoices] = useState(null);
  const [amountInputs, setAmountInputs] = useState({});
  const [amountBusy, setAmountBusy] = useState(null);
  const [payEnabled, setPayEnabled] = useState(false);
  // Espace d'échange : modération des annonces (visibles sans sélectionner de cliente)
  const [board, setBoard] = useState(null);
  useEffect(() => {
    api.get("/admin/board").then((r) => setBoard(r.data.items || [])).catch(() => setBoard([]));
  }, []);
  const deleteBoardPost = async (id) => {
    try {
      await api.delete(`/admin/board/${id}`);
      setBoard((prev) => (prev || []).filter((p) => p.id !== id));
      toast.success("Annonce supprimée.");
    } catch { toast.error("Suppression impossible."); }
  };
  useEffect(() => {
    fetch(`${API}/public/config`).then((r) => r.json()).then((d) => setPayEnabled(!!d.payments_enabled)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!selected) { setClientInvoices(null); return; }
    setClientInvoices(null);
    setAmountInputs({});
    api.get(`/portal/invoices?client_id=${selected.id}`)
      .then((r) => setClientInvoices(r.data.items || []))
      .catch(() => setClientInvoices([]));
  }, [selected?.id]);

  const saveInvoiceAmount = async (fileId) => {
    const raw = String(amountInputs[fileId] ?? "").trim().replace(",", ".");
    const amount = parseFloat(raw);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Montant invalide — ex. 480 ou 480,50."); return; }
    setAmountBusy(fileId);
    try {
      await api.put(`/admin/invoices/${fileId}/amount`, { amount });
      toast.success(`Montant enregistré : ${fmtEur(amount)} — le client peut désormais payer en ligne.`);
      setClientInvoices((prev) => (prev || []).map((it) => (it.id === fileId ? { ...it, amount } : it)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enregistrement impossible.");
    } finally { setAmountBusy(null); }
  };

  // Pastille de non-lus du Café du 30 (salon fermé : vérification toutes les 30 s)
  useEffect(() => {
    if (cafeOpen) { setCafeUnread(0); return; }
    const fetchUnread = () => api.get("/chat/messages").then((r) => setCafeUnread(r.data.unread)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [cafeOpen]);

  const load = () => {
    setLoading(true);
    api.get("/admin/clients")
      .then((r) => setClients([...r.data.clients].sort(byCode)))
      .catch(() => toast.error("Chargement des clients impossible."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormErr(""); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ code: c.code, name: c.name || "", email: c.email, password: "", drive_url: c.drive_url || "", extra_emails: (c.extra_emails || []).join(", ") }); setFormErr(""); setShowForm(true); };

  const saveClient = async (e) => {
    e.preventDefault();
    setSaving(true); setFormErr("");
    try {
      const extraEmails = form.extra_emails.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
      if (editing) {
        const payload = { code: form.code, name: form.name, email: form.email, drive_url: form.drive_url, extra_emails: extraEmails };
        if (form.password) payload.password = form.password;
        const r = await api.patch(`/admin/clients/${editing.id}`, payload);
        setClients((prev) => prev.map((c) => (c.id === editing.id ? r.data : c)).sort(byCode));
        setActiveLetter(letterOf(r.data.code));
        if (selected?.id === editing.id) setSelected(r.data);
        toast.success("Client mis à jour.");
      } else {
        const r = await api.post("/admin/clients", { ...form, extra_emails: extraEmails });
        setClients((prev) => [...prev, r.data].sort(byCode));
        setActiveLetter(letterOf(r.data.code));
        toast.success("Client créé.");
      }
      setShowForm(false);
    } catch (err) {
      setFormErr(formatApiErrorDetail(err.response?.data?.detail) || "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  const deleteClient = async (c) => {
    if (!window.confirm(`Supprimer le client ${c.code} (${c.name || c.email}) et tous ses documents ?`)) return;
    await api.delete(`/admin/clients/${c.id}`);
    setClients((prev) => prev.filter((x) => x.id !== c.id));
    if (selected?.id === c.id) setSelected(null);
    toast.success("Client supprimé.");
  };

  const submitBulk = async () => {
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = await api.post("/admin/clients/drive-links", { text: bulkText });
      setBulkResult(r.data);
      load();
    } catch { toast.error("Import impossible."); }
    finally { setBulkBusy(false); }
  };

  const submitXls = async () => {
    if (!xlsFile) return;
    setXlsBusy(true); setXlsResult(null);
    try {
      const fd = new FormData();
      fd.append("file", xlsFile);
      const r = await api.post("/admin/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setXlsResult(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import impossible."); }
    finally { setXlsBusy(false); }
  };

  const exportClients = async () => {
    setExportBusy(true);
    try {
      const r = await api.get("/admin/clients/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clients-export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé.");
    } catch (e) { toast.error("Export impossible."); }
    finally { setExportBusy(false); }
  };

  const byLetter = {};
  clients.forEach((c) => { const L = letterOf(c.code); (byLetter[L] = byLetter[L] || []).push(c); });
  const allLetters = byLetter["#"] ? [...LETTERS, "#"] : LETTERS;
  const presentLetters = allLetters.filter((L) => byLetter[L]?.length);
  const currentLetter = activeLetter && byLetter[activeLetter]?.length ? activeLetter : presentLetters[0];

  return (
    <div className="min-h-screen bg-ink text-[#F3F4F6] font-body flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-80 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0B2A52] flex flex-col">
        <div className="p-6 lg:p-8 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="font-serif text-xl">Le 30 <span className="text-gold italic">du mois</span></p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold mt-1">Administration</p>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" title="Déconnexion" className="text-[#A0B2CE] hover:text-red-400 transition-colors"><LogOut className="w-5 h-5" /></button>
        </div>

        <div className="p-6 lg:p-8 flex items-center justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A0B2CE] flex items-center gap-2"><Users className="w-4 h-4" /> Clients ({clients.length})</p>
          <button onClick={openCreate} data-testid="new-client-btn" title="Crée un nouveau client : code à 4 caractères, nom, adresses email, mot de passe provisoire généré automatiquement" className="inline-flex items-center gap-2 bg-gold text-ink px-3 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-[#B5952F] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Nouveau
          </button>
        </div>

        {stats && (
          <p className="px-6 lg:px-8 -mt-3 pb-4 font-mono text-[10px] tracking-wide text-[#A0B2CE]" data-testid="visit-stats">
            Visites du site : <span className="text-gold">{stats.visits_today}</span> aujourd'hui · <span className="text-gold">{stats.visits_month}</span> ce mois · <span className="text-gold">{stats.visits_total}</span> au total
          </p>
        )}

        {/* Index alphabétique */}
        {!loading && clients.length > 0 && (
          <div className="px-5 pb-3 flex flex-wrap gap-1" data-testid="alphabet-index">
            {allLetters.map((L) => {
              const n = byLetter[L]?.length || 0;
              return (
                <button key={L} disabled={!n} onClick={() => setActiveLetter(L)} data-testid={`letter-${L}`}
                  title={n ? `${n} client${n > 1 ? "s" : ""}` : "Aucun client"}
                  className={`w-7 h-7 rounded font-mono text-[11px] font-bold transition-colors ${!n ? "text-white/15 cursor-default" : L === currentLetter ? "bg-gold text-ink" : "text-gold/70 hover:bg-white/10"}`}>
                  {L}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-auto px-4 pb-6 space-y-1" data-testid="clients-list">
          {loading ? (
            <p className="px-4 font-mono text-xs text-gold animate-pulse">CHARGEMENT…</p>
          ) : clients.length === 0 ? (
            <p className="px-4 text-sm text-[#A0B2CE]">Aucun client. Créez-en un.</p>
          ) : (byLetter[currentLetter] || []).map((c) => (
            <div key={c.id} onClick={() => setSelected(c)} data-testid={`client-${c.code}`}
              className={`group px-4 py-3 rounded-md cursor-pointer transition-colors ${selected?.id === c.id ? "bg-gold/15 border border-gold/40" : "hover:bg-white/[0.04] border border-transparent"}`}>
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      data-testid={`drive-link-dot-${c.code}`}
                      title={c.drive_url ? "Lien Drive configuré" : "Lien Drive manquant"}
                      className={`w-2 h-2 rounded-full shrink-0 ${c.drive_url ? "bg-emerald-400" : "bg-white/20"}`}
                    />
                    <p className="font-mono text-sm text-gold tracking-widest">{c.code}</p>
                  </div>
                  <p className="text-sm truncate">{c.name || "—"}</p>
                  <p className="text-[11px] text-[#A0B2CE] truncate">{c.email}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`edit-${c.code}`} title="Modifier la fiche de ce client (nom, emails, lien Drive…)" className="w-8 h-8 rounded flex items-center justify-center text-[#A0B2CE] hover:text-gold"><Pencil className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteClient(c); }} data-testid={`delete-client-${c.code}`} title="Supprimer définitivement ce client et ses documents internes" className="w-8 h-8 rounded flex items-center justify-center text-[#A0B2CE] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Import en masse des liens Drive */}
        <div className="p-6 lg:p-8 border-t border-white/10">
          <button onClick={() => setShowBulk((v) => !v)} data-testid="bulk-links-toggle"
            title="Configure le « raccourci bureau » (lien Google Drive) de plusieurs clients d'un coup, en collant des lignes « CODE + lien »"
            className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
            <Link2 className="w-4 h-4" /> Import liens Drive
          </button>
          {showBulk && (
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-[#A0B2CE] leading-relaxed">
                Collez vos lignes « CODE + lien » (le code et le lien peuvent aussi être sur deux lignes). Seuls les clients existants sont configurés.
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                data-testid="bulk-links-input"
                rows={6}
                placeholder={"Exemple :\nABCD https://drive.google.com/drive/folders/…\nEFGH ; https://drive.google.com/drive/folders/…"}
                className="w-full bg-ink/60 border border-white/15 focus:border-gold outline-none rounded-md p-3 text-xs font-mono text-[#F3F4F6] placeholder:text-[#A0B2CE]/50 transition-colors"
              />
              <button onClick={submitBulk} disabled={bulkBusy || !bulkText.trim()} data-testid="bulk-links-submit"
                title="Applique les liens collés ci-dessus aux clients existants (les codes inconnus sont ignorés)"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Configurer les liens"}
              </button>
              {bulkResult && (
                <div className="text-xs font-mono space-y-1.5" data-testid="bulk-links-result">
                  {bulkResult.updated?.length > 0 && (
                    <p className="text-emerald-400" data-testid="bulk-links-updated">Configurés : {bulkResult.updated.join(", ")}</p>
                  )}
                  {bulkResult.unknown?.length > 0 && (
                    <p className="text-amber-400" data-testid="bulk-links-unknown">Codes inconnus (créez d'abord le compte) : {bulkResult.unknown.join(", ")}</p>
                  )}
                  {bulkResult.invalid?.length > 0 && (
                    <p className="text-red-400" data-testid="bulk-links-invalid">Lignes illisibles : {bulkResult.invalid.join(" | ")}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Import en masse de clients (Excel) */}
        <div className="p-6 lg:p-8 border-t border-white/10">
          <button onClick={() => setShowImportXls((v) => !v)} data-testid="import-clients-toggle"
            title="Crée des clients en masse depuis un classeur Excel (.xlsx). Si un code existe déjà, les adresses email de la ligne sont simplement rattachées au client — rien d'autre n'est modifié"
            className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
            <Users className="w-4 h-4" /> Import clients (Excel)
          </button>
          {showImportXls && (
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-[#A0B2CE] leading-relaxed">
                Classeur .xlsx — colonne 1 : code (4 car.), colonne 2 : nom, colonne 3 : email (plusieurs adresses possibles, séparées par des virgules : la 1re devient l'adresse principale), colonne 4 : lien Drive (facultatif). La ligne d'en-tête est ignorée. Si le code existe déjà, les adresses de la ligne sont simplement rattachées au client existant. Les mots de passe sont générés automatiquement : envoyez-les ensuite client par client via « Envoyer les identifiants ».
              </p>
              <input type="file" accept=".xlsx" onChange={(e) => setXlsFile(e.target.files?.[0] || null)} data-testid="import-clients-file"
                className="w-full text-xs text-[#A0B2CE] file:mr-3 file:rounded-full file:border file:border-gold/40 file:bg-transparent file:text-gold file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.12em] hover:file:bg-gold hover:file:text-ink file:transition-colors file:cursor-pointer" />
              <button onClick={submitXls} disabled={xlsBusy || !xlsFile} data-testid="import-clients-submit"
                title="Lance l'import du classeur sélectionné — aucun email n'est envoyé aux clients créés"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                {xlsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importer les clients"}
              </button>
              {xlsResult && (
                <div className="text-xs font-mono space-y-1.5" data-testid="import-clients-result">
                  <p className="text-emerald-400" data-testid="import-clients-created">
                    Créés : {xlsResult.created?.length || 0}{xlsResult.created?.length ? ` (${xlsResult.created.map((c) => c.code).join(", ")})` : ""}
                  </p>
                  {xlsResult.merged?.length > 0 && (
                    <p className="text-gold" data-testid="import-clients-merged">
                      Adresses secondaires rattachées : {xlsResult.merged.map((m) => `${m.code} (+${m.added.length})`).join(", ")}
                    </p>
                  )}
                  {xlsResult.skipped?.length > 0 && (
                    <div className="text-amber-400 space-y-0.5" data-testid="import-clients-skipped">
                      <p>Ignorés :</p>
                      {xlsResult.skipped.map((s, i) => (
                        <p key={i}>· ligne {s.line}{s.code ? ` ${s.code}` : ""} — {s.reason}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button onClick={exportClients} disabled={exportBusy} data-testid="export-clients-btn"
                title="Télécharge un Excel de tous les clients (sauvegarde ou synchronisation d'un autre environnement). Les mots de passe ne sont jamais exportés."
                className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exporter les clients
              </button>
              <button onClick={syncEmails} disabled={syncBusy} data-testid="sync-emails-btn"
                title="Lit le classeur « Clients-Notifications » (Google Drive) et rattache les adresses supplémentaires aux clients existants — sans jamais toucher aux mots de passe ni aux adresses principales"
                className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                {syncBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailPlus className="w-4 h-4" />} Sync emails secondaires
              </button>
              {syncResult && (
                <div className="text-xs font-mono space-y-1.5" data-testid="sync-emails-result">
                  <p className="text-emerald-400">Mis à jour : {syncResult.updated?.length || 0}{syncResult.updated?.length ? ` (${syncResult.updated.map((u) => `${u.code} +${u.added.length}`).join(", ")})` : ""}</p>
                  <p className="text-[#A0B2CE]">Déjà à jour : {syncResult.unchanged}</p>
                  {syncResult.unknown_codes?.length > 0 && (
                    <p className="text-amber-400">Codes inconnus en base : {syncResult.unknown_codes.join(", ")}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Estimateur de coût de paie */}
        <div className="p-6 lg:p-8 border-t border-white/10 space-y-2.5">
          <button onClick={() => setShowEstimator(true)} data-testid="estimator-open-btn"
            title="Estime le net et le coût total d'un bulletin à partir du brut"
            className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
            <Calculator className="w-4 h-4" /> Estimateur de coût
          </button>
          <button onClick={() => setShowCession(true)} data-testid="cession-open-btn"
            title="Calcule un prix de cession pour 1 date (plateau + transport + frais généraux), hors V.H.R., hors droits et hors rémunérations producteur et diffuseur"
            className="w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
            <Calculator className="w-4 h-4" /> Prix de cession
          </button>
        </div>

        {/* Le Café du 30 */}
        <div className="p-6 lg:p-8 border-t border-white/10">
          <button onClick={() => setCafeOpen((v) => !v)} data-testid="cafe-open-btn"
            title="Salon de discussion commun : tous les utilisateurs connectés (vous et vos clients) peuvent y échanger"
            className="relative w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">
            <Coffee className="w-4 h-4" /> Le Café du 30
            {!cafeOpen && cafeUnread > 0 && (
              <span data-testid="cafe-unread-badge" className="absolute -top-1.5 -right-1.5 bg-gold text-ink text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{cafeUnread}</span>
            )}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10" data-testid="admin-main">
        {cafeOpen ? (
          <div className="p-6 lg:p-10 h-screen" data-testid="cafe-section">
            <CafeChat onClose={() => setCafeOpen(false)} />
          </div>
        ) : !selected ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-24">
            <FolderOpen className="w-12 h-12 text-gold/50 mb-6" strokeWidth={1} />
            <h1 className="font-serif text-3xl mb-2">Espace administrateur</h1>
            <p className="text-[#A0B2CE] max-w-sm">Sélectionnez un client à gauche pour gérer ses 6 dossiers, ou créez un nouveau client.</p>
            {board && board.length > 0 && (
              <div className="mt-10 w-full max-w-2xl border border-white/10 rounded-xl p-5 bg-white/[0.02] text-left" data-testid="admin-board-section">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="font-serif text-xl">Espace d'échange — annonces en cours</h2>
                  <span className="font-mono text-[11px] bg-gold text-ink rounded-full px-2.5 py-0.5" data-testid="admin-board-count">{board.length}</span>
                </div>
                <div className="space-y-3">
                  {board.map((p) => (
                    <div key={p.id} className="flex items-start gap-3 border border-white/10 rounded-lg px-4 py-3" data-testid={`admin-board-post-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-serif text-gold truncate">{p.title}</p>
                        <p className="text-[11px] text-[#A0B2CE] font-mono">
                          {p.author_name} · {p.author_email} · {new Date(p.created_at).toLocaleDateString("fr-FR")}
                          {p.expired && <span className="text-[#E74C3C]"> · expirée</span>}
                          {p.has_attachment && <span> · 📎 {p.attachment_name}</span>}
                        </p>
                        <p className="text-sm text-[#A0B2CE] mt-1 line-clamp-2">{p.body}</p>
                      </div>
                      <button onClick={() => deleteBoardPost(p.id)} title="Supprimer cette annonce" data-testid={`admin-board-delete-${p.id}`}
                        className="w-8 h-8 rounded-full border border-[#C0392B]/50 text-[#E74C3C] flex items-center justify-center hover:bg-[#C0392B] hover:text-white transition-colors shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <motion.div key={selected.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
              <div>
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Gestion de l'espace client</p>
                <h1 className="font-serif text-4xl">{selected.name || "Client"} <span className="text-gold">· {selected.code}</span></h1>
                <p className="text-[#A0B2CE] mt-1">{selected.email}</p>
                {selected.extra_emails?.length > 0 && (
                  <p className="text-[11px] text-[#A0B2CE]/80 mt-0.5" data-testid="client-extra-emails">
                    Aussi connectable avec : {selected.extra_emails.join(", ")}
                  </p>
                )}
                <div className="mt-3 space-y-1 font-mono text-[11px] tracking-wide">
                  <p className="text-[#A0B2CE]" data-testid="client-login-stats">
                    Connexions : {selected.login_count > 0
                      ? <><span className="text-gold">{selected.login_count}</span> · dernière le {new Date(selected.last_login_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</>
                      : <span className="text-white/40">jamais connecté</span>}
                  </p>
                  <p className="text-[#A0B2CE]" data-testid="client-invoices-stats">
                    « À payer » : {selected.last_invoices_view_at
                      ? <>consulté le {new Date(selected.last_invoices_view_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · <span className="text-gold">{selected.invoices_view_count}</span> fois</>
                      : <span className="text-white/40">jamais consulté</span>}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <button onClick={() => openEdit(selected)} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold hover:border-gold transition-colors">
                  <Pencil className="w-4 h-4" /> Modifier le client
                </button>
                <button onClick={sendCredentials} disabled={credBusy} data-testid="send-credentials-btn"
                  title="Génère un nouveau mot de passe et envoie les identifiants par email à ce client uniquement"
                  className="inline-flex items-center gap-2 border border-gold/50 px-4 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] text-gold hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                  {credBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Envoyer les identifiants
                </button>
              </div>
            </div>

            {/* Factures en attente de règlement — même contenu que l'onglet « À payer » du client */}
            <div className="mb-8 border border-[#C0392B]/30 rounded-xl p-5 bg-white/[0.02]" data-testid="admin-invoices-section">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-[#E74C3C]" />
                <h2 className="font-serif text-xl">À payer</h2>
                {clientInvoices && clientInvoices.filter((i) => !i.paid).length > 0 && (
                  <span className="font-mono text-[11px] bg-[#C0392B] text-white rounded-full px-2.5 py-0.5" data-testid="admin-invoices-count">{clientInvoices.filter((i) => !i.paid).length}</span>
                )}
              </div>
              {clientInvoices === null ? (
                <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT…</p>
              ) : clientInvoices.length === 0 ? (
                <p className="text-sm text-[#A0B2CE]" data-testid="admin-invoices-empty">Aucune facture en attente de règlement.</p>
              ) : (
                <div className="space-y-2" data-testid="admin-invoices-list">
                  {clientInvoices.map((it) => (
                    <div key={it.id} className={`flex flex-wrap items-center gap-x-3 gap-y-2 border rounded-lg px-4 py-2.5 ${it.paid ? "border-emerald-500/30 bg-emerald-500/[0.03]" : "border-[#C0392B]/25 bg-white/[0.02]"}`} data-testid={`admin-invoice-${it.id}`}>
                      <FileText className={`w-4 h-4 shrink-0 ${it.paid ? "text-emerald-400" : "text-[#E74C3C]"}`} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{it.name}</p>
                        {payEnabled && it.amount != null && <p className="font-mono text-[11px] text-gold" data-testid={`admin-invoice-amount-label-${it.id}`}>{fmtEur(it.amount)}</p>}
                      </div>
                      {payEnabled && (it.paid ? (
                        <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-500/50 text-emerald-400 font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1" data-testid={`admin-invoice-paid-${it.id}`}>
                          Payée{it.paid_at ? ` le ${new Date(it.paid_at).toLocaleDateString("fr-FR")}` : ""}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <input
                            type="text" inputMode="decimal" placeholder={it.amount ? String(it.amount).replace(".", ",") : "Montant €"}
                            value={amountInputs[it.id] ?? ""}
                            onChange={(e) => setAmountInputs((p) => ({ ...p, [it.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") saveInvoiceAmount(it.id); }}
                            data-testid={`admin-invoice-amount-${it.id}`}
                            className="w-24 bg-white/5 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs font-mono text-white placeholder:text-[#5A6B8C] focus:outline-none focus:border-gold/60"
                          />
                          <button onClick={() => saveInvoiceAmount(it.id)} disabled={amountBusy === it.id}
                            title="Enregistrer le montant TTC — le bouton « Payer » apparaît alors chez le client"
                            data-testid={`admin-invoice-amount-save-${it.id}`}
                            className="inline-flex items-center gap-1.5 bg-gold text-[#0A0B10] px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                            {amountBusy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Valider
                          </button>
                        </div>
                      ))}
                      <a href={`${API}/portal/invoices/${it.id}/download?auth=${localStorage.getItem("l30m_token")}`} target="_blank" rel="noopener noreferrer"
                        title="Consulter la facture" data-testid={`admin-invoice-download-${it.id}`}
                        className="w-8 h-8 rounded-full border border-[#C0392B]/50 text-[#E74C3C] flex items-center justify-center hover:bg-[#C0392B] hover:text-white transition-colors shrink-0">
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <FileManager clientId={selected.id} isAdmin />
          </motion.div>
        )}
      </main>

      {/* Estimateur de coût de paie */}
      {showEstimator && <CostEstimator onClose={() => setShowEstimator(false)} />}
      {showCession && <CessionSimulator onClose={() => setShowCession(false)} />}

      {/* Client form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0B2A52] border border-white/10 rounded-xl p-8" data-testid="client-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">{editing ? "Modifier le client" : "Nouveau client"}</h2>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-[#A0B2CE]" /></button>
            </div>
            <form onSubmit={saveClient} className="space-y-5">
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Code à 4 caractères</label>
                <input type="text" required maxLength={4} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="form-code"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 text-lg tracking-[0.3em] uppercase transition-colors" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Nom / compagnie</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Emails secondaires (séparés par des virgules)</label>
                <input type="text" value={form.extra_emails} onChange={(e) => setForm({ ...form, extra_emails: e.target.value })} data-testid="form-extra-emails"
                  placeholder="ex. associe@cie.fr, direction@cie.fr"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors placeholder:text-white/25" />
                <p className="text-[10px] text-[#A0B2CE]/70 mt-1.5">Le client peut se connecter avec ces adresses aussi (même mot de passe). Les emails automatiques partent uniquement vers l'adresse principale.</p>
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Mot de passe {editing ? "(laisser vide pour conserver)" : "(facultatif — généré automatiquement)"}</label>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password"
                  placeholder={editing ? "" : "Laisser vide = généré par le site"}
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors placeholder:text-white/25" />
              </div>
              <div>
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Lien Drive du client (raccourci bureau)</label>
                <input type="url" value={form.drive_url} onChange={(e) => setForm({ ...form, drive_url: e.target.value })} data-testid="form-drive-url"
                  placeholder="https://drive.google.com/drive/folders/…"
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 transition-colors" />
              </div>
              {formErr && <p className="text-red-400 text-sm font-mono" data-testid="form-error">{formErr}</p>}
              <button type="submit" disabled={saving} data-testid="save-client-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink py-3 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Enregistrer" : "Créer le client"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
